import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Leaf, Globe, ClipboardCheck, Waves, Search, Trophy, Star,
  ChevronRight, Phone, Mail, MapPin, Building2, Check, ArrowDown,
  Scale, BarChart3, FileText, Users, Calendar, Monitor, BookOpen,
  Briefcase, Plus
} from 'lucide-react';

/* ─── FAQ DATA ─── */
const faqItems = [
  {
    q: "Mon entreprise est-elle obligée de faire un bilan carbone en PF ?",
    a: "En Polynésie française, le seuil BEGES est abaissé à 250 salariés (vs 500 en métropole). Les structures publiques de plus de 250 agents sont aussi concernées. Pour les PME en dessous de ce seuil, le bilan carbone reste volontaire, mais devient un critère d'accès aux marchés des grands donneurs d'ordres soumis à la CSRD pour leur Scope 3."
  },
  {
    q: "Le télétravail s'applique-t-il au secteur privé en PF ?",
    a: "Oui. La Loi du Pays n° 2022-39 du 10 novembre 2022 encadre le télétravail pour le secteur privé polynésien. Elle s'applique sur la base du volontariat, nécessite un avenant au contrat de travail et définit les droits et obligations de l'employeur et du salarié. La délibération d'octobre 2024 s'applique spécifiquement à la fonction publique."
  },
  {
    q: "Peut-on financer ces missions via un dispositif d'aide ?",
    a: "Plusieurs pistes existent : les missions RSE peuvent être intégrées dans une démarche d'amélioration continue éligible à certains appels à projets du Pays. Pour l'organisation hybride, les coûts de conseil peuvent être imputés sur certains postes budgétaires RH. Nous étudions avec vous, au cas par cas, les possibilités de financement disponibles en Polynésie française."
  },
  {
    q: "Ces missions incluent-elles les déplacements inter-îles ?",
    a: "Les tarifs indiqués sont valables pour des structures basées à Tahiti (Papeete et communes limitrophes). Pour les structures ayant des sites sur d'autres îles (Moorea, Raiatea, Bora Bora, etc.), les déplacements sont facturés en sus sur devis. Nous proposons aussi un format hybride : partie en présentiel, partie en visioconférence pour optimiser les coûts."
  },
  {
    q: "Quel est le délai entre la signature et le démarrage ?",
    a: "En général, 2 à 3 semaines après la signature de la convention et le versement de l'acompte. Ce délai nous permet de préparer les outils, de collecter les informations préalables et de planifier les agendas des intervenants. Pour les urgences (appel d'offres, audit interne imminents), nous évaluons ensemble la faisabilité d'un démarrage accéléré."
  },
  {
    q: "Peut-on combiner ces missions avec d'autres prestations AERH ?",
    a: "Absolument. Ces missions se combinent naturellement avec notre offre de formation Management Agile (AERH Formations Tahiti), nos audits RH classiques, ou la gestion de paie externalisée (Moana Paye). Nous construisons avec vous un programme d'accompagnement global et cohérent sur 12 à 24 mois."
  }
];

/* ─── FORMULE CARD COMPONENT ─── */
const FormuleCard = ({ badge, badgeIcon: BadgeIcon, name, tagline, price, priceUnit, duration, features, cta, ctaGold, featured, dark }) => (
  <div className={`rounded-2xl overflow-hidden border flex flex-col relative transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl ${
    dark
      ? featured
        ? 'bg-[#F59E0B]/8 border-[#F59E0B] shadow-lg shadow-[#F59E0B]/15'
        : 'bg-white/5 border-white/12 hover:border-white/25'
      : featured
        ? 'border-[#F59E0B] shadow-lg shadow-[#F59E0B]/15 bg-white'
        : 'border-slate-200 bg-white'
  }`} data-testid={`formule-card-${name.toLowerCase().replace(/\s+/g, '-')}`}>
    {featured && (
      <span className="absolute top-4 right-4 bg-[#F59E0B] text-white text-[0.67rem] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full z-10">
        Recommandé
      </span>
    )}
    <div className="px-6 pt-7 pb-5">
      <div className={`inline-flex items-center gap-2 text-xs font-bold tracking-wider uppercase px-3 py-1.5 rounded-full mb-3.5 ${
        dark ? 'bg-white/8 text-white/60' : featured ? 'bg-[#F59E0B]/12 text-[#F59E0B]' : 'bg-[#0F766E]/10 text-[#0F766E]'
      }`}>
        <BadgeIcon className="w-3.5 h-3.5" />
        {badge}
      </div>
      <h3 className={`font-serif text-xl font-bold mb-1.5 ${dark ? 'text-white' : 'text-[#1D3D3D]'}`}>{name}</h3>
      <p className={`text-sm leading-relaxed ${dark ? 'text-white/55' : 'text-slate-500'}`}>{tagline}</p>
    </div>
    <div className={`px-6 py-4 border-t border-b ${dark ? 'border-white/10' : 'border-slate-100'}`}>
      <div className={`font-serif text-3xl font-bold ${dark ? 'text-white' : 'text-[#1D3D3D]'}`}>{price}</div>
      <div className={`text-xs mt-1 ${dark ? 'text-white/50' : 'text-slate-500'}`}>{priceUnit}</div>
    </div>
    <div className="px-6 py-5 flex-1">
      <div className={`flex items-center gap-2 text-xs mb-4 ${dark ? 'text-white/50' : 'text-slate-500'}`}>
        <Calendar className="w-3.5 h-3.5" />
        {duration}
      </div>
      <ul className="space-y-0.5">
        {features.map((f, i) => (
          <li key={i} className={`flex items-start gap-2.5 text-sm py-1.5 border-b last:border-b-0 leading-relaxed ${
            dark ? 'text-white/85 border-white/7' : 'text-slate-700 border-slate-50'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${f.gold ? 'bg-[#F59E0B]' : 'bg-[#0F766E]'}`} />
            {f.text}
          </li>
        ))}
      </ul>
    </div>
    <div className="px-6 pb-5">
      <a href="#contact-rse" className={`block text-center py-3 px-6 rounded-xl text-sm font-semibold tracking-wide transition-all duration-200 ${
        ctaGold
          ? 'bg-[#F59E0B] text-white hover:bg-[#d4820f] hover:scale-[1.02]'
          : dark
            ? 'bg-white/12 border border-white/25 text-white hover:bg-white/20'
            : 'bg-[#2D5A5A] text-white hover:bg-[#1D3D3D] hover:scale-[1.02]'
      }`}>
        {cta}
      </a>
    </div>
  </div>
);

/* ─── FAQ ITEM COMPONENT ─── */
const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden transition-shadow hover:shadow-md">
      <button onClick={() => setOpen(!open)} className="w-full text-left px-5 py-5 flex justify-between items-center gap-4">
        <span className="text-sm font-semibold text-[#1D3D3D] leading-snug">{q}</span>
        <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
          open ? 'bg-[#2D5A5A] text-white rotate-45' : 'bg-[#D4E8E8] text-[#2D5A5A]'
        }`}>
          <Plus className="w-3.5 h-3.5" />
        </span>
      </button>
      <div ref={ref} className="overflow-hidden transition-all duration-300" style={{ maxHeight: open ? ref.current?.scrollHeight + 'px' : '0px' }}>
        <div className="px-5 pb-5 text-sm text-slate-500 leading-relaxed">{a}</div>
      </div>
    </div>
  );
};

/* ─── MAIN PAGE ─── */
export default function RseHybridePage() {
  const [visible, setVisible] = useState(new Set());

  useEffect(() => {
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          setVisible(prev => new Set([...prev, e.target.dataset.reveal]));
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    document.querySelectorAll('[data-reveal]').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const rev = (id, delay = '') => ({
    'data-reveal': id,
    className: `transition-all duration-700 ${delay} ${visible.has(id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-7'}`
  });

  return (
    <div className="bg-white" data-testid="rse-hybride-page">

      {/* ══════════ HERO ══════════ */}
      <section className="relative bg-[#1D3D3D] min-h-[90vh] flex flex-col justify-center items-center overflow-hidden px-6 py-20">
        <div className="absolute inset-0 pointer-events-none opacity-[0.13]">
          <svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" className="w-full h-full absolute inset-0">
            <path d="M0,400 C200,300 400,500 600,380 C800,260 1000,450 1200,350 C1300,300 1380,320 1440,310 L1440,900 L0,900 Z" fill="rgba(74,138,138,1)"/>
            <path d="M0,550 C150,450 350,600 550,490 C750,380 950,560 1150,460 C1300,390 1400,420 1440,400 L1440,900 L0,900 Z" fill="rgba(45,90,90,1)"/>
            <path d="M0,200 C300,180 600,280 900,200 C1100,150 1300,220 1440,190" fill="none" stroke="rgba(240,150,30,.3)" strokeWidth="1.5"/>
          </svg>
        </div>
        <div className="absolute w-[800px] h-[800px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(74,138,138,.35) 0%, transparent 70%)', top: '-200px', right: '-200px' }} />
        <div className="absolute w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(240,150,30,.12) 0%, transparent 70%)', bottom: '-150px', left: '-100px' }} />

        <div className="animate-[fadeUp_0.7s_ease_both] inline-flex items-center gap-2.5 bg-[#F59E0B]/15 border border-[#F59E0B]/40 text-[#F7B85A] text-[0.72rem] font-semibold tracking-[0.18em] uppercase px-4 py-2 rounded-full mb-7">
          <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full" />
          AERH Consulting — Nouvelles offres 2026
        </div>

        <h1 className="animate-[fadeUp_0.8s_0.1s_ease_both] font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl text-white text-center leading-[1.1] max-w-[880px]">
          L'entreprise <em className="italic text-[#F7B85A]">responsable</em><br/>commence par le bon conseil
        </h1>

        <p className="animate-[fadeUp_0.8s_0.2s_ease_both] mt-5 text-base text-white/65 text-center max-w-[600px] leading-relaxed font-light">
          Deux nouvelles missions d'accompagnement pensées pour les structures polynésiennes —
          audit RSE &amp; bilan carbone, organisation hybride conforme à la Loi du Pays 2022-39.
        </p>

        <div className="animate-[fadeUp_0.8s_0.3s_ease_both] flex flex-wrap gap-3 justify-center mt-9">
          {[
            { icon: Leaf, label: 'Audit RSE & Bilan Carbone' },
            { icon: Globe, label: 'Organisation Hybride PF' },
            { icon: Star, label: 'Pack Impact Durable' }
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2 bg-white/8 border border-white/15 text-white/85 px-4 py-2 rounded-full text-sm">
              <Icon className="w-4 h-4 text-[#F59E0B]" /> {label}
            </div>
          ))}
        </div>

        <div className="absolute bottom-9 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/35 text-[0.72rem] tracking-[0.12em] uppercase">
          <div className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent animate-pulse" />
          Découvrir
        </div>
      </section>

      {/* ══════════ INTRO BAND ══════════ */}
      <div className="bg-slate-50 border-t-[3px] border-[#F59E0B] py-12 px-6">
        <div {...rev('intro')} className={`max-w-[860px] mx-auto grid md:grid-cols-2 gap-10 items-center ${visible.has('intro') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-7'} transition-all duration-700`}>
          <div>
            <h2 className="font-serif text-2xl font-semibold text-[#1D3D3D] mb-3">Deux enjeux, un seul partenaire de confiance</h2>
            <p className="text-slate-500 leading-relaxed font-light">
              En 2026, les entreprises polynésiennes font face à deux transformations simultanées : la montée des attentes RSE de leurs partenaires et donneurs d'ordres, et la nécessité d'encadrer un travail hybride qui s'est installé sans cadre. AERH Consulting vous accompagne sur les deux fronts.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { num: '250', label: 'Seuil salariés BEGES Outre-mer' },
              { num: '~10', label: 'Entreprises PF avec télétravail structuré' },
              { num: '25+', label: "Ans d'expertise RH en Polynésie" },
              { num: '2022', label: 'Loi du Pays Télétravail PF' }
            ].map(s => (
              <div key={s.label} className="text-center p-5 bg-white rounded-xl border border-slate-200">
                <span className="font-serif text-3xl font-bold text-[#F59E0B] leading-none block">{s.num}</span>
                <span className="text-xs text-slate-500 mt-1 block font-medium">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════ OFFRE 1 — RSE ══════════ */}
      <section id="rse-section" className="bg-white">
        <div className="max-w-[1100px] mx-auto px-6 py-20">
          <div {...rev('rse-label')}>
            <div className="flex items-center gap-3.5 mb-4">
              <div className="w-10 h-0.5 bg-[#F59E0B]" />
              <span className="text-[0.72rem] font-semibold tracking-[0.2em] uppercase text-[#F59E0B]">Offre 01</span>
            </div>
          </div>
          <h2 {...rev('rse-title')} className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1D3D3D] leading-tight mb-4 ${visible.has('rse-title') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-7'} transition-all duration-700`}>
            Audit RSE & <em className="italic text-[#3D7A7A]">Bilan Carbone</em><br/>PME Polynésienne
          </h2>
          <p {...rev('rse-intro')} className={`text-slate-500 font-light text-base leading-relaxed max-w-[620px] mb-12 ${visible.has('rse-intro') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-7'} transition-all duration-700`}>
            La Polynésie française est en première ligne du changement climatique. Vos partenaires,
            donneurs d'ordres et talents de demain regardent ce que vous faites concrètement.
            Nous transformons cette contrainte en avantage concurrentiel durable.
          </p>

          {/* Context cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {[
              {
                icon: ClipboardCheck, title: 'Obligation BEGES Outre-mer',
                text: "En Polynésie française, le seuil d'obligation du Bilan d'Émissions de Gaz à Effet de Serre est abaissé à 250 salariés (vs 500 en métropole) pour tenir compte de la vulnérabilité climatique des territoires insulaires.",
                highlight: "Amende jusqu'à 50 000 € en cas de manquement"
              },
              {
                icon: Waves, title: 'Enjeu stratégique pour la PF',
                text: "Même sans obligation légale directe, vos donneurs d'ordres soumis à la CSRD vous demanderont vos données carbone pour leur propre Scope 3. Agir maintenant, c'est sécuriser vos marchés de demain.",
                highlight: 'Levier de différenciation & marque employeur'
              }
            ].map(c => (
              <div key={c.title} className="bg-[#1D3D3D] rounded-2xl p-7 relative overflow-hidden">
                <div className="absolute -top-10 -right-10 w-36 h-36 bg-white/4 rounded-full" />
                <c.icon className="w-7 h-7 text-[#F59E0B] mb-3.5" />
                <h4 className="font-serif text-lg font-semibold text-white mb-2">{c.title}</h4>
                <p className="text-sm text-white/65 leading-relaxed font-light">{c.text}</p>
                <span className="mt-3 inline-block bg-[#F59E0B]/20 text-[#F7B85A] rounded-md px-2.5 py-1 text-xs font-semibold">{c.highlight}</span>
              </div>
            ))}
          </div>

          {/* RSE Formules */}
          <div className="grid md:grid-cols-3 gap-5 mb-12">
            <FormuleCard badge="INITIA" badgeIcon={Leaf} name="Diagnostic RSE Express" tagline="Le point de départ idéal pour connaître votre maturité RSE et prioriser vos actions" price="185 000 F CFP" priceUnit="Forfait tout inclus — HT = TTC (exonéré TVA)" duration="2 à 3 jours de mission" features={[
              { text: 'Grille RSE 30 indicateurs (Environnement, Social, Gouvernance)' },
              { text: 'Rapport de maturité RSE avec score par axe' },
              { text: 'Cartographie des risques et opportunités' },
              { text: "Plan d'action prioritaire : 5 actions à 90 jours" },
              { text: 'Attestation AERH valorisable en communication', gold: true }
            ]} cta="Demander un devis" />
            <FormuleCard badge="EMPREINTE" badgeIcon={Globe} name="Bilan Carbone Complet" tagline="La mission de référence : mesure normée des Scopes 1, 2 et 3, plan de décarbonation inclus" price="480 000 F CFP" priceUnit="Forfait — HT = TTC · Déplacements inter-îles en sus" duration="6 à 10 semaines de mission" featured features={[
              { text: 'Collecte de données : énergie, transport, déchets, achats, RH' },
              { text: 'Calcul BEGES normé — méthode ADEME / GHG Protocol' },
              { text: "Rapport d'émissions complet (tCO₂e) Scopes 1, 2, 3" },
              { text: 'Plan de décarbonation : objectifs, actions, indicateurs' },
              { text: 'Tableau de bord Excel de suivi annuel' },
              { text: 'Accompagnement à la communication RSE interne & externe', gold: true }
            ]} cta="Démarrer la mission" ctaGold />
            <FormuleCard badge="TRAJECTOIRE" badgeIcon={BarChart3} name="Pilotage RSE Annuel" tagline="L'externalisation complète de votre fonction RSE avec suivi trimestriel et rapport annuel" price="95 000 F CFP" priceUnit="/mois · Engagement 12 mois · Soit 1,14 M F CFP HT/an" duration="Mission récurrente 12 mois" features={[
              { text: 'Bilan carbone initial complet (formule EMPREINTE)' },
              { text: 'Revues trimestrielles de suivi des indicateurs' },
              { text: 'Rapport annuel de durabilité format VSME / CSRD-compatible' },
              { text: 'Formation référent RSE interne (1 journée)' },
              { text: 'Représentation possible auprès des instances dirigeantes', gold: true }
            ]} cta="En savoir plus" />
          </div>

          {/* RSE Cibles */}
          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
            <h3 className="font-serif text-xl font-semibold text-[#1D3D3D] mb-5">Structures prioritairement concernées en Polynésie française</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="rse-cibles-table">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-2.5 bg-[#1D3D3D] text-white font-semibold text-xs tracking-wider uppercase rounded-l-lg">Vous êtes...</th>
                    <th className="text-left px-4 py-2.5 bg-[#1D3D3D] text-white font-semibold text-xs tracking-wider uppercase rounded-r-lg">Formule conseillée</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['CHPF, CPS, OPH, Trésorerie', 'Empreinte ou Trajectoire'],
                    ["Commune (Faa'a, Pirae, Mahina...)", 'Trajectoire'],
                    ['Grande entreprise privée (Brasserie de Tahiti, Air Tahiti Nui...)', 'Empreinte'],
                    ['Hôtel, groupe nautique, opérateur touristique', 'Initia ou Empreinte'],
                    ["PME sous-traitante d'un grand donneur d'ordres", 'Initia']
                  ].map(([who, formula], i) => (
                    <tr key={i} className={i % 2 === 1 ? 'bg-white/70' : ''}>
                      <td className="px-4 py-3 border-b border-slate-200 last:border-b-0">{who}</td>
                      <td className="px-4 py-3 border-b border-slate-200 last:border-b-0 font-medium text-[#2D5A5A]">{formula}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ WAVE DIVIDER ══════════ */}
      <div className="w-full overflow-hidden leading-[0]">
        <svg viewBox="0 0 1440 80" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ height: '80px' }}>
          <path d="M0,0 C360,80 1080,0 1440,60 L1440,80 L0,80 Z" fill="#1D3D3D"/>
        </svg>
      </div>

      {/* ══════════ OFFRE 2 — HYBRIDE ══════════ */}
      <section id="hybride-section" className="bg-[#1D3D3D]">
        <div className="max-w-[1100px] mx-auto px-6 py-20">
          <div className="flex items-center gap-3.5 mb-4">
            <div className="w-10 h-0.5 bg-[#F7B85A]" />
            <span className="text-[0.72rem] font-semibold tracking-[0.2em] uppercase text-[#F7B85A]">Offre 02</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-tight mb-4">
            Audit Organisation <em className="italic text-[#F7B85A]">Hybride</em><br/>& Télétravail
          </h2>
          <p className="text-white/65 font-light text-base leading-relaxed max-w-[620px] mb-12">
            La Loi du Pays 2022-39 existe. La délibération de 2024 encadre la fonction publique.
            Pourtant, seule une dizaine d'entreprises polynésiennes ont structuré leur télétravail.
            C'est votre fenêtre d'opportunité.
          </p>

          {/* Loi banner */}
          <div className="flex items-start gap-5 bg-[#F59E0B]/10 border border-[#F59E0B]/30 border-l-4 border-l-[#F59E0B] rounded-xl p-5 mb-11">
            <Scale className="w-6 h-6 text-[#F59E0B] flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-serif text-base font-semibold text-[#F7B85A] mb-1.5">Cadre légal polynésien maîtrisé</h4>
              <p className="text-sm text-white/70 leading-relaxed font-light">
                AERH Consulting connaît la Loi du Pays n° 2022-39 du 10 novembre 2022 relative au télétravail et la délibération du 17 octobre 2024 encadrant la fonction publique (jusqu'à 3 jours/semaine). Toutes nos chartes et documents sont rédigés en conformité avec le Code du travail polynésien.
              </p>
            </div>
          </div>

          {/* Hybride Formules */}
          <div className="grid md:grid-cols-3 gap-5 mb-12">
            <FormuleCard dark badge="DIAGNOS'HYBRIDE" badgeIcon={Search} name="Audit de Faisabilité" tagline="Savoir si et comment introduire le télétravail dans votre structure avant de vous lancer" price="145 000 F CFP" priceUnit="Forfait — HT = TTC" duration="1 à 2 jours de mission" features={[
              { text: 'Cartographie des postes éligibles juridiquement et opérationnellement' },
              { text: 'Analyse des risques RH : isolement, surcharge, équité' },
              { text: 'Vérification de conformité Loi du Pays 2022-39' },
              { text: 'Feuille de route hybride personnalisée' }
            ]} cta="Demander un devis" />
            <FormuleCard dark featured badge="HYBRIDE PRO" badgeIcon={Trophy} name="Déploiement Complet" tagline="Du diagnostic à la mise en oeuvre : tout le dispositif pour lancer votre organisation hybride" price="380 000 F CFP" priceUnit="Forfait — HT = TTC" duration="4 à 6 semaines de mission" features={[
              { text: 'Audit organisationnel complet (processus, outils, culture)' },
              { text: 'Charte télétravail rédigée & conforme Code du travail PF' },
              { text: 'Avenant type au contrat de travail sécurisé' },
              { text: 'Guide du manager hybride contextualisé PF' },
              { text: 'Formation managers (1 demi-journée incluse)' },
              { text: 'Tableau de bord de suivi présentiel/distanciel', gold: true }
            ]} cta="Démarrer la mission" ctaGold />
            <FormuleCard dark badge="EXCELLENCE" badgeIcon={Star} name="Transformation Complète" tagline="Pour les grandes structures multi-sites, avec spécificité inter-archipels et suivi semestriel" price="750 000 F CFP" priceUnit="Mission initiale + 45 000 F CFP/mois suivi" duration="3 mois + suivi semestriel" features={[
              { text: 'Tout de la formule HYBRIDE PRO' },
              { text: 'Baromètre social initial (enquête salariés)' },
              { text: 'Ateliers co-construction par équipe' },
              { text: 'Politique télétravail inter-îles (Moorea, Raiatea...)' },
              { text: 'Formation RH interne' },
              { text: "Bilan d'impact à 6 mois avec rapport", gold: true }
            ]} cta="En savoir plus" />
          </div>

          {/* Hybride Cibles */}
          <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
            <h3 className="font-serif text-xl font-semibold text-white mb-5">Structures prioritairement concernées</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="hybride-cibles-table">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-2.5 bg-white/12 text-white font-semibold text-xs tracking-wider uppercase rounded-l-lg">Vous êtes...</th>
                    <th className="text-left px-4 py-2.5 bg-white/12 text-white font-semibold text-xs tracking-wider uppercase rounded-r-lg">Formule conseillée</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['CHPF, CPS, OPH (multi-sites, inter-archipels)', 'Excellence'],
                    ['Commune, établissement public (> 50 agents)', 'Hybride PRO'],
                    ['Cabinet juridique, étude notariale, cabinet comptable', "Diagnos'Hybride"],
                    ['PME tertiaire (conseil, services, communication)', 'Hybride PRO'],
                    ['Groupe hôtelier ou touristique — fonctions support', "Diagnos'Hybride"]
                  ].map(([who, formula], i) => (
                    <tr key={i} className={i % 2 === 1 ? 'bg-white/4' : ''}>
                      <td className="px-4 py-3 border-b border-white/7 last:border-b-0 text-white/80">{who}</td>
                      <td className="px-4 py-3 border-b border-white/7 last:border-b-0 text-[#F7B85A] font-medium">{formula}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ WAVE INVERSE ══════════ */}
      <div className="w-full overflow-hidden leading-[0] rotate-180 -mb-px">
        <svg viewBox="0 0 1440 80" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ height: '80px' }}>
          <path d="M0,0 C360,80 1080,0 1440,60 L1440,80 L0,80 Z" fill="#1D3D3D"/>
        </svg>
      </div>

      {/* ══════════ PACK COMBO ══════════ */}
      <section id="pack-section" className="bg-[#FEF3E2]">
        <div className="max-w-[1100px] mx-auto px-6 py-20">
          <div className="flex items-center gap-3.5 justify-center mb-4">
            <div className="w-10 h-0.5 bg-[#F59E0B]" />
            <span className="text-[0.72rem] font-semibold tracking-[0.2em] uppercase text-[#F59E0B]">Offre combinée</span>
            <div className="w-10 h-0.5 bg-[#F59E0B]" />
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1D3D3D] leading-tight mb-4 text-center max-w-[700px] mx-auto">
            Pack <em className="italic text-[#3D7A7A]">Impact Durable</em>
          </h2>
          <p className="text-slate-500 font-light text-base leading-relaxed text-center max-w-[620px] mx-auto mb-12">
            Les deux missions fondamentales de l'entreprise responsable en 2026, réunies en un seul engagement.
          </p>

          <div className="bg-white rounded-3xl overflow-hidden shadow-2xl shadow-[#1D3D3D]/10 grid lg:grid-cols-2" data-testid="pack-impact-durable">
            {/* Left */}
            <div className="bg-gradient-to-br from-[#1D3D3D] to-[#2D5A5A] p-10 lg:p-12 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#F59E0B]/10 rounded-full" />
              <div className="text-[0.7rem] tracking-[0.2em] uppercase text-[#F7B85A] font-semibold mb-4 flex items-center gap-2">
                <Star className="w-3.5 h-3.5" /> Offre exclusive AERH Consulting
              </div>
              <h3 className="font-serif text-4xl font-bold text-white leading-none mb-2.5">IMPACT<br/>DURABLE</h3>
              <p className="text-sm text-white/60 font-light mb-8 leading-relaxed">
                Diagnostic RSE Express + Audit de Faisabilité Hybride — deux missions, un seul interlocuteur, une vision globale de votre transformation.
              </p>
              <div className="bg-white/10 rounded-2xl p-5 mb-6">
                <div className="text-sm text-white/45 line-through mb-1">330 000 F CFP HT séparément</div>
                <div className="font-serif text-4xl font-bold text-[#F7B85A] leading-none">295 000 <span className="text-base font-sans font-normal">F CFP HT</span></div>
                <div className="inline-flex items-center gap-1.5 bg-[#F59E0B]/25 text-[#F7B85A] rounded-full px-3 py-1 text-xs font-bold mt-2">
                  <Check className="w-3 h-3" /> Économie de 35 000 F CFP
                </div>
              </div>
              <div className="space-y-2">
                {[
                  'Grille RSE 30 indicateurs + rapport de maturité',
                  "Plan d'action 5 priorités à 90 jours",
                  'Cartographie des postes télétravaillables',
                  'Feuille de route hybride conforme Loi du Pays 2022-39',
                  'Restitution co-dirigeants avec tableau de bord synthèse'
                ].map(item => (
                  <div key={item} className="flex items-center gap-3 text-sm text-white/80">
                    <div className="w-5 h-5 bg-[#F59E0B]/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-[#F59E0B]" />
                    </div>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* Right */}
            <div className="p-10 lg:p-12 flex flex-col justify-between">
              <div>
                <h3 className="font-serif text-xl font-semibold text-[#1D3D3D] mb-6">Comment se déroule la mission ?</h3>
                <div className="space-y-4 mb-8">
                  {[
                    { num: '1', title: 'Réunion de cadrage (J1)', desc: "Présentation des enjeux, collecte des documents existants (bilan RH, organigramme, contrats), validation du périmètre." },
                    { num: '2', title: 'Audits terrain (J2–J3)', desc: "Grille RSE appliquée sur site + entretiens managers sur l'organisation du travail et les freins au télétravail." },
                    { num: '3', title: 'Restitution dirigeants (J4)', desc: "Présentation des rapports RSE et Hybride, tableau de bord synthèse, recommandations priorisées et plan d'action." },
                    { num: '4', title: 'Livrables finaux (J5)', desc: "Remise des rapports complets, attestation AERH, guide de mise en oeuvre autonome des premières actions." }
                  ].map(s => (
                    <div key={s.num} className="flex gap-4 items-start">
                      <div className="w-8 h-8 flex-shrink-0 bg-[#1D3D3D] text-white rounded-lg flex items-center justify-center text-sm font-bold">{s.num}</div>
                      <div>
                        <h4 className="text-sm font-semibold text-[#1D3D3D] mb-0.5">{s.title}</h4>
                        <p className="text-xs text-slate-500 leading-relaxed font-light">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-50 border-l-[3px] border-[#F59E0B] rounded-r-lg p-4 text-sm text-slate-500 italic leading-relaxed mb-7">
                « En une mission, vous pilotez votre impact environnemental ET votre organisation du travail — les deux leviers les plus attendus par vos collaborateurs et vos partenaires en 2026. »
              </div>

              <div className="space-y-3">
                <a href="#contact-rse" className="block text-center bg-[#F59E0B] text-white py-3.5 px-8 rounded-xl text-sm font-bold tracking-wide transition-all hover:bg-[#d4820f] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#F59E0B]/40">
                  Réserver le Pack Impact Durable
                </a>
                <a href="#contact-rse" className="block text-center border-[1.5px] border-[#2D5A5A] text-[#2D5A5A] py-3 px-8 rounded-xl text-sm font-medium transition-all hover:bg-[#2D5A5A] hover:text-white">
                  Demander plus d'informations
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ FAQ ══════════ */}
      <section className="bg-slate-50 py-20 px-6">
        <div className="max-w-[1100px] mx-auto">
          <div className="flex items-center gap-3.5 mb-4">
            <div className="w-10 h-0.5 bg-[#F59E0B]" />
            <span className="text-[0.72rem] font-semibold tracking-[0.2em] uppercase text-[#F59E0B]">Questions fréquentes</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#1D3D3D] leading-tight mb-10">
            Ce que vous nous <em className="italic text-[#3D7A7A]">demandez</em> souvent
          </h2>
          <div className="grid md:grid-cols-2 gap-3.5">
            {faqItems.map((item, i) => <FaqItem key={i} {...item} />)}
          </div>
        </div>
      </section>

      {/* ══════════ CTA CONTACT ══════════ */}
      <section id="contact-rse" className="bg-[#1D3D3D]">
        <div className="max-w-[780px] mx-auto text-center px-6 py-24">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-tight mb-4">
            Prêt à faire le premier pas vers une entreprise <em className="italic text-[#F7B85A]">plus responsable</em> ?
          </h2>
          <p className="text-white/60 font-light text-base leading-relaxed mb-10">
            Contactez-nous pour un échange de 30 minutes sans engagement. Nous analysons votre situation et vous orientons vers la formule la plus adaptée à vos enjeux et à votre budget.
          </p>
          <div className="flex gap-3.5 justify-center flex-wrap">
            <a href="mailto:contact@groupeaerhpolynesie.com" className="bg-[#F59E0B] text-white py-3.5 px-8 rounded-xl text-sm font-bold tracking-wide transition-all hover:bg-[#d4820f] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#F59E0B]/40 min-w-[220px]">
              Nous contacter par email
            </a>
            <a href="tel:+68987737815" className="border-[1.5px] border-white/25 text-white/75 py-3.5 px-8 rounded-xl text-sm font-medium transition-all hover:bg-white/8 hover:text-white hover:border-white/50 flex items-center gap-2">
              <Phone className="w-4 h-4" /> 87 73 78 15
            </a>
          </div>
          <div className="mt-11 flex justify-center gap-9 flex-wrap">
            <div className="flex items-center gap-2.5 text-white/55 text-sm"><MapPin className="w-4 h-4" /> 10 Rue Georges LAGARDE — Papeete</div>
            <div className="flex items-center gap-2.5 text-white/55 text-sm"><Building2 className="w-4 h-4" /> RCS 24 112B — N° Tahiti F80302</div>
            <div className="flex items-center gap-2.5 text-white/55 text-sm"><Star className="w-4 h-4" /> AERH Consulting · Groupe AERH Polynésie</div>
          </div>
        </div>
      </section>
    </div>
  );
}
